<template>
  <div class="consulta-chat">

    <!-- HEADER DE LA SECCIÓN (oculto dentro del modal) -->
    <div class="cc-section-header">
      <div class="cc-badge">
        <Icon icon="mdi:clipboard-search" class="text-sm" />
        <span>Consulta tu Pedido</span>
      </div>
      <h2 class="cc-title">
        ¿Cómo va tu <span class="cc-text-gradient">pedido</span>?
      </h2>
      <p class="cc-desc">
        Chatea con nuestro asistente y te digo el estado de tu pedido al instante.
      </p>
    </div>

    <!-- TELÉFONO DE CHAT -->
    <div class="cc-phone">
      <div class="cc-wa-head">
        <div class="cc-av">
          <img src="@/assets/logo/extragas-logo.png" alt="">
        </div>
        <div class="cc-wa-info">
          <strong>{{ empresa.nombre }}</strong>
          <small>Asistente · en línea</small>
        </div>
        <button class="cc-reset" @click="reiniciar" title="Empezar de nuevo">
          <Icon icon="mdi:refresh" class="text-base" />
        </button>
      </div>

      <!-- MENSAJES -->
      <div class="cc-chat" ref="chatRef" aria-live="polite"></div>

      <!-- BOTONES DE OPCIÓN -->
      <div v-if="tipoEsperado === 'botones' && botonesOpciones.length" class="cc-options">
        <button
          v-for="(op, i) in botonesOpciones"
          :key="i"
          type="button"
          class="cc-option-btn"
          @click="tocarBoton(op)"
        >
          {{ op.label }}
        </button>
      </div>

      <!-- BARRA DE ESCRITURA -->
      <form
        v-if="tipoEsperado === 'texto' || tipoEsperado === 'ambos'"
        class="cc-input-bar"
        @submit.prevent="enviarMensaje"
      >
        <input
          v-model="textoEscrito"
          type="text"
          class="cc-input-txt"
          :placeholder="placeholderActual"
          autocomplete="off"
          ref="inputRef"
          :disabled="!esperandoRespuesta"
        />
        <button
          type="submit"
          class="cc-input-send"
          :disabled="!textoEscrito.trim() || !esperandoRespuesta"
          aria-label="Enviar"
        >
          <Icon icon="mdi:send" class="text-base" />
        </button>
      </form>
    </div>
  </div>
</template>

<script>
import { Icon } from "@iconify/vue";
import { useToast } from "vue-toastification";
import { obtenerDatosEmpresa } from '@/utils/tenantConfig';
import { obtenerPedidosRecarga, obtenerDeliveries, obtenerDeliveryAsignado } from '@/api/gas.js';

export default {
  name: 'ConsultaPedidoChat',
  components: { Icon },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      seq: 0,
      textoEscrito: '',
      placeholderActual: 'Escribe un mensaje…',
      esperandoRespuesta: false,
      botonesOpciones: [],
      resolverActual: null,
      tipoEsperado: 'texto',
      deliveries: [],
      empresa: {
        nombre: 'EXTRA GAS',
        rif: 'J-41023119-0'
      }
    };
  },
  async mounted() {
    await this.cargarDeliveries();
    this.$nextTick(() => {
      this.cargarEmpresa();
      this.reiniciar();
    });
  },
  methods: {
    cargarEmpresa() {
      try {
        const d = obtenerDatosEmpresa();
        if (d) {
          this.empresa = {
            nombre: d.nombreComercial || d.nombre || this.empresa.nombre,
            rif: d.rif || this.empresa.rif
          };
        }
      } catch (e) { /* noop */ }
    },

    async cargarDeliveries() {
      try {
        const res = await obtenerDeliveries();
        if (res?.success) {
          this.deliveries = Array.isArray(res.data) ? res.data : [];
        }
      } catch (e) {
        console.warn('No se pudieron cargar los deliverys:', e);
        this.deliveries = [];
      }
    },

    // ⭐ Intento local (si el pedido ya trae idDelivery o los campos inline)
    obtenerDeliveryDelPedido(pedido) {
      if (!pedido) return null;

      if (pedido.idDelivery) {
        const found = this.deliveries.find(d => d.idDelivery === pedido.idDelivery);
        if (found) return found;
      }

      if (pedido.nombreDelivery || pedido.telefonoDelivery) {
        return {
          idDelivery: pedido.idDelivery,
          nombre: pedido.nombreDelivery || '',
          apellido: pedido.apellidoDelivery || '',
          telefono: pedido.telefonoDelivery || '',
          tipoVehiculo: pedido.tipoVehiculoDelivery || '',
          placaVehiculo: pedido.placaDelivery || '',
          calificacionPromedio: pedido.calificacionDelivery || null
        };
      }

      return null;
    },

    scrollBottom() {
      this.$nextTick(() => {
        const el = this.$refs.chatRef;
        if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
        const inp = this.$refs.inputRef;
        if (inp && this.esperandoRespuesta && this.tipoEsperado !== 'botones') inp.focus();
      });
    },

    msg(texto, who = 'in') {
      if (!this.$refs.chatRef) return;
      const el = document.createElement('div');
      el.className = 'cc-msg ' + who;
      el.innerHTML = texto;
      this.$refs.chatRef.appendChild(el);
      this.scrollBottom();
      return el;
    },

    bot(texto, delay = 700) {
      const my = this.seq;
      return new Promise((resolve) => {
        if (!this.$refs.chatRef) return resolve();
        const t = document.createElement('div');
        t.className = 'cc-typing';
        t.innerHTML = '<i></i><i></i><i></i>';
        this.$refs.chatRef.appendChild(t);
        this.scrollBottom();
        setTimeout(() => {
          t.remove();
          if (my !== this.seq) return resolve();
          this.msg(texto);
          resolve();
        }, delay);
      });
    },

    esperarRespuesta(opts = {}) {
      return new Promise((resolve) => {
        this.placeholderActual = opts.placeholder || 'Escribe un mensaje…';
        this.botonesOpciones = opts.botones || [];
        this.tipoEsperado = opts.tipo || 'texto';
        this.esperandoRespuesta = true;
        this.resolverActual = resolve;
        this.scrollBottom();
      });
    },

    enviarMensaje() {
      const txt = (this.textoEscrito || '').trim();
      if (!txt || !this.esperandoRespuesta || !this.resolverActual) return;
      this.msg(this.escapar(txt), 'out');
      this.textoEscrito = '';
      const resolver = this.resolverActual;
      this.resolverActual = null;
      this.esperandoRespuesta = false;
      this.botonesOpciones = [];
      resolver(txt);
    },

    tocarBoton(op) {
      if (!this.esperandoRespuesta || !this.resolverActual) return;
      this.msg(this.escapar(op.label), 'out');
      const resolver = this.resolverActual;
      this.resolverActual = null;
      this.esperandoRespuesta = false;
      this.botonesOpciones = [];
      resolver(op.value);
    },

    escapar(s) {
      return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
    },

    async reiniciar() {
      if (!this.$refs.chatRef) return;
      this.seq++;
      this.textoEscrito = '';
      this.esperandoRespuesta = false;
      this.botonesOpciones = [];
      this.resolverActual = null;
      this.$refs.chatRef.innerHTML = '';

      await this.bot('¡Hola! 👋 Soy el asistente de <b>' + this.empresa.nombre + '</b>.');
      await this.bot('Te ayudo a consultar el estado de tu pedido. ¿Empezamos?', 500);

      const r = await this.esperarRespuesta({
        botones: [{ label: '🔍 Consultar mi pedido', value: 'si' }],
        tipo: 'botones'
      });

      if (r === 'si') await this.flujoConsulta();
    },

    async flujoConsulta() {
      await this.bot('Dime el <b>N° de tu pedido</b>.');
      await this.bot('💡 Lo encuentras en tu comprobante (Ej: <b>GAS-0001</b>)', 500);
      const numeroPedido = await this.pedirNumeroPedido();

      await this.bot('Perfecto. Ahora dime tu <b>cédula</b> para verificar.');
      const cedula = await this.pedirCedula();

      await this.bot('🔍 Buscando tu pedido en el sistema…', 600);
      await this.buscarPedido(numeroPedido, cedula);
    },

    async pedirNumeroPedido() {
      const v = await this.esperarRespuesta({
        placeholder: 'Ej: GAS-0001',
        tipo: 'texto'
      });
      const limpio = v.toUpperCase().replace(/[^A-Z0-9-]/g, '');
      if (limpio.length < 5) {
        await this.bot('Ese número no parece válido. Intenta de nuevo (Ej: GAS-0001).');
        return this.pedirNumeroPedido();
      }
      return limpio;
    },

    async pedirCedula() {
      const v = await this.esperarRespuesta({
        placeholder: 'Ej: V-12345678',
        tipo: 'texto'
      });
      if (v.replace(/\D/g, '').length < 6) {
        await this.bot('La cédula debe tener al menos 6 números. Intenta de nuevo.');
        return this.pedirCedula();
      }
      const valor = v.toUpperCase().replace(/[^VEJPG0-9]/g, '');
      const letra = (valor.match(/^[VEJPG]/) || ['V'])[0];
      const num = valor.replace(/[^0-9]/g, '');
      return letra + '-' + num;
    },

    async buscarPedido(numeroPedido, cedula) {
      try {
        const res = await obtenerPedidosRecarga();

        if (!res?.success || !Array.isArray(res.data)) {
          await this.bot('⚠️ No pude consultar el sistema. Intenta de nuevo en un momento.');
          return this.ofrecerReintentar();
        }

        const numeroBuscado = numeroPedido.replace(/[^0-9]/g, '');
        const pedido = res.data.find(p => {
          const numP = String(p.numeroPedido || '').toUpperCase().replace(/[^0-9A-Z]/g, '');
          const numPBuscado = numeroPedido.replace(/[^0-9A-Z]/g, '');
          return numP === numPBuscado || numP.replace(/\D/g, '') === numeroBuscado;
        });

        if (!pedido) {
          await this.bot(
            '😕 No encontré ningún pedido con el número <b>' + this.escapar(numeroPedido) + '</b>.\n\n' +
            'Verifica el número e intenta de nuevo.'
          );
          return this.ofrecerReintentar();
        }

        const cedulaPedido = this.normalizarCedula(pedido.cedulaCliente || pedido.cedula || '');
        const cedulaIngresada = this.normalizarCedula(cedula);

        if (!cedulaPedido || cedulaPedido !== cedulaIngresada) {
          await this.bot(
            '🔒 La cédula que me diste no coincide con el titular del pedido.\n\n' +
            'Por seguridad no puedo mostrarte la información.'
          );
          return this.ofrecerReintentar();
        }

        await this.bot('✅ <b>¡Pedido encontrado!</b>');
        await this.mostrarResultado(pedido);

      } catch (error) {
        console.error('Error consultando:', error);
        await this.bot('⚠️ Hubo un error al consultar. Intenta de nuevo.');
        return this.ofrecerReintentar();
      }
    },

    async mostrarResultado(p) {
      const estado = p.estado || 'PENDIENTE';
      const estadoNormalizado = String(estado).toUpperCase().replace(/\s+/g, '_');
      const emoji = this.emojiEstado(estadoNormalizado);
      const label = this.labelEstado(estadoNormalizado);

      await this.bot(
        emoji + ' <b>Pedido #' + (p.numeroPedido || '—') + '</b>\n' +
        '👤 Cliente: ' + (p.nombreCliente || '—') + '\n' +
        '💰 Total: Bs. ' + this.formatearNumero(p.totalBs, 2) + '\n' +
        (p.nombreZona ? '📍 Zona: ' + p.nombreZona + '\n' : '') +
        '📊 Estado: <b>' + label + '</b>'
      );

      if (estadoNormalizado === 'PENDIENTE') {
        await this.bot('⏳ Estamos verificando tu pedido. Pronto te confirmaremos el método de pago.');
      } else if (estadoNormalizado === 'CONFIRMADO') {
        await this.bot(
          '💳 <b>¡Tu pedido está aprobado!</b>\n\n' +
          'Ahora debes realizar el pago por <b>Pago Móvil</b>:\n' +
          '📞 Teléfono: <b>0424 5254408</b>\n' +
          '🏦 Banco: <b>Banco de Tesoro</b>\n' +
          '🆔 Cédula: <b>V-26.035.840</b>\n' +
          '💰 Monto: <b>Bs. ' + this.formatearNumero(p.totalBs, 2) + '</b>\n\n' +
          'Cuando tengas tu número de referencia, repórtalo en el sistema.'
        );
        const r = await this.esperarRespuesta({
          botones: [
            { label: '📄 Reportar mi pago', value: 'reportar' },
            { label: '✅ Entendido', value: 'ok' }
          ],
          tipo: 'botones'
        });
        if (r === 'reportar') {
          this.$router.push({ path: '/gas/reportar-pago', query: { pedido: p.idPedidoRecarga } });
          return;
        }
      } else if (estadoNormalizado === 'PAGO_PENDIENTE') {
        await this.bot('⏳ Recibimos tu reporte de pago. Un operador lo está verificando. Te avisaremos cuando esté confirmado.');
      } else if (estadoNormalizado === 'PAGO_VERIFICADO') {
        await this.bot('✅ <b>¡Pago confirmado!</b> Tu pedido será asignado a un delivery en breve.');

      } else if (['ASIGNADO', 'EN_CAMINO', 'EN_PROCESO'].includes(estadoNormalizado)) {
        // ⭐ PEDIR EL DELIVERY AL BACKEND
        console.log('🟢 [mostrarResultado] Estado en camino:', estadoNormalizado, '- Pedido ID:', p.idPedidoRecarga);

        let delivery = null;

        // Intento 1: catálogo local (si el pedido ya trae idDelivery)
        delivery = this.obtenerDeliveryDelPedido(p);

        // Intento 2: pedirlo al backend
        if (!delivery) {
          try {
            const resD = await obtenerDeliveryAsignado(p.idPedidoRecarga);
            console.log('📦 [obtenerDeliveryAsignado] Respuesta:', resD);

            if (resD?.success && resD.data) {
              delivery = resD.data;
              console.log('✅ Delivery encontrado:', delivery);
            } else {
              console.warn('⚠️ El endpoint devolvió sin delivery:', resD);
            }
          } catch (e) {
            console.error('❌ Error pidiendo delivery:', e);
          }
        }

        if (delivery) {
          let msgDelivery =
            '🚚 <b>¡Tu pedido está en camino!</b>\n\n' +
            '<b>🛵 Tu repartidor:</b>\n' +
            '👤 ' + (delivery.nombre || '') + ' ' + (delivery.apellido || '') + '\n' +
            '📞 ' + (delivery.telefono || 'Sin teléfono');

          if (delivery.tipoVehiculo || delivery.placaVehiculo) {
            msgDelivery += '\n🏍️ ' + (delivery.tipoVehiculo || 'Vehículo');
            if (delivery.placaVehiculo) {
              msgDelivery += ' · ' + delivery.placaVehiculo;
            }
          }

          if (delivery.calificacionPromedio) {
            msgDelivery += '\n⭐ ' + delivery.calificacionPromedio;
          }

          await this.bot(msgDelivery);

          // ⭐ Botón de WhatsApp
          if (delivery.telefono) {
            await this.bot(
              '💬 ¿Necesitas coordinar con el repartidor? Puedes escribirle por WhatsApp.',
              500
            );

            const r = await this.esperarRespuesta({
              botones: [
                { label: '📱 Contactar por WhatsApp', value: 'whatsapp' },
                { label: '✅ Entendido', value: 'ok' }
              ],
              tipo: 'botones'
            });

            if (r === 'whatsapp') {
              this.abrirWhatsAppDelivery(delivery, p);
            }
          }
        } else {
          await this.bot('🚚 <b>¡Tu pedido está en camino!</b>\nPronto llegará a tu puerta.');
        }

      } else if (estadoNormalizado === 'COMPLETADO') {
        await this.bot('🎉 <b>¡Pedido completado!</b>\nGracias por tu compra.');
      } else if (estadoNormalizado === 'CANCELADO') {
        await this.bot('❌ Este pedido fue cancelado. Si tienes dudas, contáctanos por WhatsApp.');
      }

      const s = await this.esperarRespuesta({
        botones: [
          { label: '🔍 Consultar otro pedido', value: 'otro' },
          { label: '🛒 Hacer un pedido nuevo', value: 'nuevo' }
        ],
        tipo: 'botones'
      });
      if (s === 'otro') return this.flujoConsulta();
      if (s === 'nuevo') {
        this.$router.push('/gas/registro');
        return;
      }
      await this.bot('¡Gracias por usar EXTRA GAS! 🔥');
    },

    abrirWhatsAppDelivery(delivery, pedido) {
      if (!delivery?.telefono) return;

      let phone = String(delivery.telefono).replace(/\D/g, '');

      if (phone.length === 10 && !phone.startsWith('58')) {
        phone = '58' + phone;
      } else if (phone.length === 11 && phone.startsWith('0')) {
        phone = '58' + phone.substring(1);
      }

      const msj =
        'Hola ' + (delivery.nombre || '') + ', soy ' +
        (pedido.nombreCliente || 'el cliente') +
        '. Estoy consultando el pedido ' +
        (pedido.numeroPedido || '') +
        '. ¿Podemos coordinar la entrega?';

      window.open(
        'https://wa.me/' + phone + '?text=' + encodeURIComponent(msj),
        '_blank'
      );
    },

    async ofrecerReintentar() {
      const r = await this.esperarRespuesta({
        botones: [
          { label: '🔁 Intentar de nuevo', value: 'retry' },
          { label: '🛒 Hacer un pedido', value: 'nuevo' }
        ],
        tipo: 'botones'
      });
      if (r === 'retry') return this.flujoConsulta();
      if (r === 'nuevo') {
        this.$router.push('/gas/registro');
      }
    },

    normalizarCedula(cedula) {
      if (!cedula) return '';
      return String(cedula)
        .toUpperCase()
        .replace(/[^VEJPG0-9]/g, '')
        .replace(/^[VEJPG]/, '');
    },

    formatearNumero(v, dec = 2) {
      if (v === null || v === undefined || v === '') return '0';
      const n = Number(v);
      if (isNaN(n)) return '0';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec
      }).format(n);
    },

    labelEstado(estado) {
      const map = {
        'PENDIENTE': 'Pendiente de confirmación',
        'CONFIRMADO': 'Aprobado — Realiza tu pago',
        'PAGO_PENDIENTE': 'Pago en verificación',
        'PAGO_VERIFICADO': 'Pago confirmado',
        'ASIGNADO': 'Asignado a delivery',
        'EN_CAMINO': 'En camino',
        'EN_PROCESO': 'Entregando',
        'COMPLETADO': 'Completado',
        'CANCELADO': 'Cancelado'
      };
      return map[estado] || estado;
    },

    emojiEstado(estado) {
      const map = {
        'PENDIENTE': '🟡',
        'CONFIRMADO': '🟠',
        'PAGO_PENDIENTE': '⏳',
        'PAGO_VERIFICADO': '✅',
        'ASIGNADO': '📦',
        'EN_CAMINO': '🚚',
        'EN_PROCESO': '🔥',
        'COMPLETADO': '🎉',
        'CANCELADO': '❌'
      };
      return map[estado] || 'ℹ️';
    }
  }
};
</script>

<style>
/* ⚠️ SIN "scoped" — para que aplique a los elementos creados con appendChild */

/* ═══════════════════════════════════════════════════════
   RESET / CONTENEDOR PRINCIPAL
═══════════════════════════════════════════════════════ */
.consulta-chat {
  font-family: 'Lexend', -apple-system, BlinkMacSystemFont, sans-serif;
  padding: 0;
  background: transparent;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

.consulta-chat .cc-section-header { display: none; }
.consulta-chat .cc-wa-head { display: none; }

.consulta-chat .cc-phone {
  max-width: 100%;
  width: 100%;
  margin: 0;
  background: #ECE5DD;
  border-radius: 0;
  overflow: hidden;
  border: 0;
  box-shadow: none;
  display: flex;
  flex-direction: column;
  flex: 1;
  height: 100%;
  min-height: 0;
}

/* ═══════════════════════════════════════════════════════
   CHAT — FONDO ELEGANTE
═══════════════════════════════════════════════════════ */
.consulta-chat .cc-chat {
  flex: 1;
  overflow-y: auto;
  padding: 18px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  color: #1B2433;
  background-color: #EFE9E1;
  background-image:
    radial-gradient(circle at 15% 25%, rgba(232, 122, 48, 0.06) 0 3px, transparent 3px),
    radial-gradient(circle at 75% 55%, rgba(93, 173, 226, 0.05) 0 2px, transparent 2px),
    radial-gradient(circle at 45% 85%, rgba(26, 42, 74, 0.04) 0 2.5px, transparent 2.5px),
    radial-gradient(circle at 88% 12%, rgba(232, 122, 48, 0.05) 0 2px, transparent 2px),
    radial-gradient(circle at 8% 72%, rgba(93, 173, 226, 0.04) 0 2.5px, transparent 2.5px),
    linear-gradient(180deg, #F2ECE4 0%, #EAE3DA 100%);
  background-size: 220px 220px, 160px 160px, 240px 240px, 200px 200px, 180px 180px, 100% 100%;
  min-height: 0;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

.consulta-chat .cc-chat::-webkit-scrollbar { width: 6px; }
.consulta-chat .cc-chat::-webkit-scrollbar-track { background: transparent; }
.consulta-chat .cc-chat::-webkit-scrollbar-thumb {
  background: rgba(26, 42, 74, 0.15);
  border-radius: 3px;
}
.consulta-chat .cc-chat::-webkit-scrollbar-thumb:hover { background: rgba(26, 42, 74, 0.3); }

/* ═══════════════════════════════════════════════════════
   BURBUJAS
═══════════════════════════════════════════════════════ */
.consulta-chat .cc-msg {
  max-width: 82%;
  padding: 11px 15px;
  border-radius: 16px;
  font-size: 0.875rem;
  line-height: 1.5;
  position: relative;
  white-space: pre-line;
  word-wrap: break-word;
  font-weight: 500;
  letter-spacing: 0.005em;
}

.consulta-chat .cc-msg.in {
  background: linear-gradient(180deg, #FFFFFF 0%, #FBFBFB 100%);
  color: #1B2433;
  align-self: flex-start;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-right-radius: 16px;
  border-bottom-left-radius: 6px;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.06),
    0 4px 12px rgba(15, 26, 47, 0.06),
    0 0 0 1px rgba(15, 26, 47, 0.03);
  animation: ccSlideInLeft 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.consulta-chat .cc-msg.out {
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: #FFFFFF;
  align-self: flex-end;
  border-top-left-radius: 16px;
  border-top-right-radius: 16px;
  border-bottom-left-radius: 16px;
  border-bottom-right-radius: 6px;
  box-shadow:
    0 2px 6px rgba(232, 122, 48, 0.28),
    0 8px 20px rgba(232, 122, 48, 0.22);
  animation: ccSlideInRight 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.consulta-chat .cc-msg b {
  font-weight: 800;
  letter-spacing: 0.01em;
}

.consulta-chat .cc-msg.in b { color: #1a2a4a; }
.consulta-chat .cc-msg.out b { color: #FFFFFF; }

@keyframes ccSlideInLeft {
  from { opacity: 0; transform: translateX(-12px) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
}

@keyframes ccSlideInRight {
  from { opacity: 0; transform: translateX(12px) scale(0.96); }
  to   { opacity: 1; transform: translateX(0) scale(1); }
}

/* ═══════════════════════════════════════════════════════
   INDICADOR "ESCRIBIENDO…"
═══════════════════════════════════════════════════════ */
.consulta-chat .cc-typing {
  align-self: flex-start;
  background: linear-gradient(180deg, #FFFFFF 0%, #FBFBFB 100%);
  border-radius: 16px;
  border-bottom-left-radius: 6px;
  padding: 14px 18px;
  display: flex;
  gap: 6px;
  align-items: center;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.06),
    0 4px 12px rgba(15, 26, 47, 0.06),
    0 0 0 1px rgba(15, 26, 47, 0.03);
  animation: ccSlideInLeft 0.35s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.consulta-chat .cc-typing i {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  animation: ccBounce 1.4s infinite ease-in-out;
  box-shadow: 0 1px 3px rgba(232, 122, 48, 0.3);
}
.consulta-chat .cc-typing i:nth-child(2) { animation-delay: 0.15s; }
.consulta-chat .cc-typing i:nth-child(3) { animation-delay: 0.3s; }

@keyframes ccBounce {
  0%, 60%, 100% { transform: translateY(0) scale(1); opacity: 0.5; }
  30% { transform: translateY(-6px) scale(1.15); opacity: 1; }
}

/* ═══════════════════════════════════════════════════════
   BOTONES DE OPCIÓN
═══════════════════════════════════════════════════════ */
.consulta-chat .cc-options {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 14px 16px 16px;
  background: linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(240, 236, 228, 0.98) 100%);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-top: 1px solid rgba(26, 42, 74, 0.06);
  flex-shrink: 0;
  max-height: 50%;
  overflow-y: auto;
  animation: ccFadeUp 0.3s ease-out;
}

.consulta-chat .cc-options::-webkit-scrollbar { width: 5px; }
.consulta-chat .cc-options::-webkit-scrollbar-thumb {
  background: rgba(26, 42, 74, 0.15);
  border-radius: 3px;
}

.consulta-chat .cc-option-btn {
  width: 100%;
  background: linear-gradient(180deg, #FFFFFF 0%, #FAFAFA 100%);
  color: #1a2a4a;
  border: 1.5px solid rgba(93, 173, 226, 0.25);
  border-radius: 14px;
  padding: 14px 18px;
  font: 700 0.9rem 'Lexend', sans-serif;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  text-align: center;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.04),
    0 2px 8px rgba(15, 26, 47, 0.04);
  letter-spacing: 0.01em;
}

.consulta-chat .cc-option-btn:hover {
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  border-color: transparent;
  color: #FFFFFF;
  transform: translateY(-2px);
  box-shadow:
    0 4px 12px rgba(232, 122, 48, 0.3),
    0 12px 28px rgba(232, 122, 48, 0.25);
}

.consulta-chat .cc-option-btn:active {
  transform: translateY(0) scale(0.98);
}

@keyframes ccFadeUp {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ═══════════════════════════════════════════════════════
   BARRA DE ESCRITURA
═══════════════════════════════════════════════════════ */
.consulta-chat .cc-input-bar {
  display: flex;
  gap: 8px;
  padding: 12px 14px;
  background: linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(248, 245, 240, 0.98) 100%);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  align-items: center;
  flex-shrink: 0;
  border-top: 1px solid rgba(26, 42, 74, 0.06);
  box-shadow: 0 -2px 12px rgba(15, 26, 47, 0.04);
}

.consulta-chat .cc-input-txt {
  flex: 1;
  background: #FFFFFF;
  border: 1.5px solid rgba(26, 42, 74, 0.08);
  outline: 0;
  border-radius: 22px;
  padding: 12px 18px;
  font: 500 0.9rem 'Lexend', sans-serif;
  color: #1B2433;
  box-shadow:
    0 1px 2px rgba(15, 26, 47, 0.04) inset,
    0 1px 3px rgba(15, 26, 47, 0.03);
  transition: all 0.2s ease;
}

.consulta-chat .cc-input-txt::placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.consulta-chat .cc-input-txt:focus {
  border-color: rgba(232, 122, 48, 0.4);
  box-shadow:
    0 0 0 3px rgba(232, 122, 48, 0.1),
    0 1px 3px rgba(15, 26, 47, 0.05);
}

.consulta-chat .cc-input-txt:disabled {
  background: #f1f5f9;
  cursor: not-allowed;
  opacity: 0.7;
}

.consulta-chat .cc-input-send {
  width: 46px;
  height: 46px;
  border: 0;
  border-radius: 50%;
  background: linear-gradient(135deg, #E87A30 0%, #FF9F5A 100%);
  color: #fff;
  cursor: pointer;
  display: grid;
  place-items: center;
  flex-shrink: 0;
  box-shadow:
    0 2px 6px rgba(232, 122, 48, 0.3),
    0 6px 16px rgba(232, 122, 48, 0.25);
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
  font-size: 1.1rem;
}

.consulta-chat .cc-input-send:hover:not(:disabled) {
  transform: scale(1.08) rotate(-8deg);
  box-shadow:
    0 4px 10px rgba(232, 122, 48, 0.4),
    0 10px 24px rgba(232, 122, 48, 0.35);
}

.consulta-chat .cc-input-send:active:not(:disabled) {
  transform: scale(0.95);
}

.consulta-chat .cc-input-send:disabled {
  opacity: 0.35;
  cursor: not-allowed;
  box-shadow: 0 2px 6px rgba(232, 122, 48, 0.15);
}

/* ═══════════════════════════════════════════════════════
   📱 MÓVIL
═══════════════════════════════════════════════════════ */
@media (max-width: 640px) {
  .consulta-chat .cc-chat {
    padding: 14px 10px;
    gap: 8px;
  }
  .consulta-chat .cc-msg {
    max-width: 88%;
    padding: 10px 14px;
    font-size: 0.86rem;
    line-height: 1.48;
  }
  .consulta-chat .cc-options {
    padding: 10px 12px 14px;
    padding-bottom: max(14px, env(safe-area-inset-bottom));
    gap: 8px;
    max-height: 45%;
  }
  .consulta-chat .cc-option-btn {
    padding: 13px 16px;
    font-size: 0.86rem;
    min-height: 48px;
    -webkit-tap-highlight-color: transparent;
  }
  .consulta-chat .cc-input-bar {
    padding: 10px 12px;
    padding-bottom: max(10px, env(safe-area-inset-bottom));
    gap: 6px;
  }
  .consulta-chat .cc-input-txt {
    font-size: 16px;
    padding: 11px 16px;
    border-radius: 20px;
  }
  .consulta-chat .cc-input-send {
    width: 44px;
    height: 44px;
  }
}

@media (max-width: 380px) {
  .consulta-chat .cc-msg {
    font-size: 0.83rem;
    padding: 9px 12px;
  }
  .consulta-chat .cc-option-btn {
    font-size: 0.83rem;
    padding: 12px 14px;
    min-height: 46px;
  }
  .consulta-chat .cc-input-send {
    width: 42px;
    height: 42px;
  }
}
</style>